import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../design-system/icons.jsx';
import { useDataTable, applyFilters } from '../../hooks/useDataTable';
import Pagination  from '../Pagination';
import ColumnHeader from './ColumnHeader';
import FilterPanel  from './FilterPanel';
import Toolbar      from './Toolbar';

function SkeletonRows({ count, columns, selectable, hasRowActions }) {
  return Array.from({ length: count }).map((_, ri) => (
    <tr key={ri} className="placeholder-glow">
      {selectable && (
        <td><span className="placeholder rounded" style={{ width: 14, height: 14 }} /></td>
      )}
      {columns.map((col, ci) => (
        <td key={col.key}>
          <span className={`placeholder col-${ci === 0 ? 8 : ci % 3 === 1 ? 10 : 6}`} />
        </td>
      ))}
      {hasRowActions && <td />}
    </tr>
  ));
}

export default function DataTable({
  storageKey,
  columns       = [],
  data          = [],
  loading       = false,
  error         = null,
  searchFields  = [],
  selected,
  onSelect,
  selectable    = false,
  defaultPageSize = 25,
  toolbarLeft,
  toolbarRight,
  rowActions,
  exportFilename,
  exportColumns,
  skeletonRows  = 6,
  emptyState,
  onRowClick,
  onCellContextMenu,
  isRowActive,
  noShell = false,
  tableState,
  onVisibleRowsChange,
  serverTotal,
  onExport,
}) {
  const { t } = useTranslation();
  // When tableState is provided (lifted state), use a null key to skip localStorage.
  const internalState = useDataTable(tableState ? null : storageKey, { pageSize: defaultPageSize });
  const {
    sort, setSort,
    search, setSearch,
    columnFilters, setColumnFilter, clearColumnFilter, clearAllFilters,
    page, setPage,
    pageSize, setPageSize,
  } = tableState ?? internalState;

  const [openPanel, setOpenPanel] = useState(null);
  const serverMode = typeof serverTotal === 'number';

  const filtered = useMemo(
    () => serverMode ? data : applyFilters(data, columns, columnFilters, search, searchFields),
    [data, columns, columnFilters, search, searchFields, serverMode]
  );

  const sorted = useMemo(() => {
    if (serverMode) return filtered;
    const filterOrder = Object.entries(columnFilters)
      .find(([, f]) => f?.order);
    const activeSort = filterOrder
      ? { col: filterOrder[0], dir: filterOrder[1].order }
      : sort;
    if (!activeSort) return filtered;
    return [...filtered].sort((a, b) => {
      let va = a[activeSort.col], vb = b[activeSort.col];
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return activeSort.dir === 'asc' ? -1 : 1;
      if (va > vb) return activeSort.dir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sort, columnFilters, serverMode]);

  // Optional escape hatch for callers that need to export/act on exactly the
  // rows the table is currently showing (search + column filters + sort
  // applied, pagination not applied) — e.g. a page-level "Export" button that
  // must match this table's own toolbar export. No-op when not provided.
  const reportedRows = useRef(null);
  useEffect(() => {
    if (!onVisibleRowsChange) { reportedRows.current = null; return; }
    const previous = reportedRows.current;
    // Parents can recreate searchFields/columns when receiving this callback.
    // A new array containing the same rows is not a new result set: notifying
    // again would feed a render -> filter -> callback -> render loop.
    if (previous?.callback === onVisibleRowsChange
      && previous.rows.length === sorted.length
      && previous.rows.every((row, index) => row === sorted[index])) return;
    reportedRows.current = { callback: onVisibleRowsChange, rows: sorted.slice() };
    onVisibleRowsChange(sorted);
  }, [sorted, onVisibleRowsChange]);

  const rowCount = serverMode ? serverTotal : sorted.length;
  const totalPages = Math.max(1, Math.ceil(rowCount / pageSize));
  const safePage   = Math.min(Math.max(1, page), totalPages);
  const pageStart  = (safePage - 1) * pageSize;
  const pageRows   = serverMode ? sorted : sorted.slice(pageStart, pageStart + pageSize);
  const colSpan    = columns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0);

  const handleFilterClick = useCallback((colKey, el) => {
    const rect = el.getBoundingClientRect();
    setOpenPanel(prev => prev?.colKey === colKey ? null : { colKey, rect });
  }, []);

  const allSelected  = selectable && pageRows.length > 0 && pageRows.every(r => selected?.has(r.id));
  const someSelected = selectable && selected?.size > 0 && !allSelected;

  const toggleAll = () => {
    if (!onSelect) return;
    const next = new Set(selected);
    pageRows.forEach(r => allSelected ? next.delete(r.id) : next.add(r.id));
    onSelect(next);
  };

  const toggleOne = (id) => {
    if (!onSelect) return;
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    onSelect(next);
  };

  return (
    <div className={noShell ? undefined : 'ui-panel'} style={{ position: 'relative' }}>
      <Toolbar
        onExport={onExport}
        loading={loading}
        columns={columns}
        columnFilters={columnFilters}
        onClear={clearColumnFilter}
        onClearAll={clearAllFilters}
        search={search}
        onSearch={setSearch}
        filteredRows={sorted}
        exportFilename={exportFilename || storageKey}
        exportColumns={exportColumns}
        toolbarLeft={toolbarLeft}
        toolbarRight={toolbarRight}
      />

      <div className="table-responsive">
        <table aria-busy={loading} className="table table-striped table-hover align-middle table-sticky mb-0">
          <thead>
            <tr>
              {selectable && (
                <th style={{ width: 36 }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allSelected}
                    ref={el => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleAll}
                  />
                </th>
              )}
              {columns.map(col => (
                <ColumnHeader
                  key={col.key}
                  col={col}
                  sort={sort}
                  onSort={setSort}
                  onFilterClick={handleFilterClick}
                  filterActive={!!columnFilters[col.key]}
                />
              ))}
              {rowActions && <th style={{ width: 1, padding: '0 8px' }} />}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows
                count={skeletonRows}
                columns={columns}
                selectable={selectable}
                hasRowActions={!!rowActions}
              />
            ) : error ? (
              <tr>
                <td colSpan={colSpan} className="p-3">
                  <div className="alert alert-danger mb-0">{error}</div>
                </td>
              </tr>
            ) : data.length === 0 && !Object.keys(columnFilters).length && !search ? (
              <tr>
                <td colSpan={colSpan}>
                  {emptyState ?? (
                    <div className="d-flex flex-column align-items-center gap-2 py-5">
                      <div className="dt-empty-icon">
                        <Icon name="noResults" size={18} strokeWidth={1.5} />
                      </div>
                      <div className="small fw-semibold text-secondary">
                        {t('table.noResults')}
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={colSpan}>
                  <div className="d-flex flex-column align-items-center gap-2 py-5">
                    <div className="dt-empty-icon">
                      <Icon name="noResults" size={18} strokeWidth={1.5} />
                    </div>
                    <div className="small fw-semibold text-secondary">
                      {t('table.noResults')}
                    </div>
                    <div className="small text-muted">
                      {t('table.noResultsHint')}
                    </div>
                    {(Object.keys(columnFilters).length > 0 || search) && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-xs mt-1"
                        onClick={() => { clearAllFilters(); setSearch(''); }}
                      >
                        {t('table.clearAllFilters')}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              pageRows.map((row, ri) => {
                const isSelected = selected?.has(row.id);
                const isActive   = isRowActive ? isRowActive(row) : false;
                const rowClass = [
                  'dt-row',
                  (isActive || isSelected) && 'table-active',
                  onRowClick && 'dt-row-clickable',
                ].filter(Boolean).join(' ');
                return (
                  <tr
                    key={row.id ?? ri}
                    className={rowClass}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {selectable && (
                      <td onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={isSelected || false}
                          onChange={() => toggleOne(row.id)}
                        />
                      </td>
                    )}
                    {columns.map(col => (
                      <td key={col.key} style={col.cellStyle}
                        onContextMenu={onCellContextMenu ? e => { e.preventDefault(); onCellContextMenu(e, row, col) } : undefined}>
                        {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '')}
                      </td>
                    ))}
                    {rowActions && (
                      <td>
                        <div className="d-flex gap-1 justify-content-end">
                          {rowActions(row)
                            .filter(a => a.show !== false)
                            .map((a, idx) => (
                              <button
                                key={`${a.label}-${idx}`}
                                type="button"
                                title={a.label}
                                onClick={a.onClick}
                                className={[
                                  'btn btn-ghost btn-xs',
                                  a.icon && 'btn-icon',
                                  a.danger && 'btn-ghost-danger',
                                ].filter(Boolean).join(' ')}
                              >
                                {a.icon ? <Icon name={a.icon} size={16} /> : a.label}
                              </button>
                            ))}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {!loading && data.length > 0 && sorted.length > 0 && (
        <Pagination
          page={safePage - 1}
          pages={totalPages}
          total={rowCount}
          limit={pageSize}
          onPage={p => setPage(p + 1)}
          onLimit={setPageSize}
          sizes={[10, 25, 50, 100]}
        />
      )}

      {openPanel && (() => {
        const col = columns.find(c => c.key === openPanel.colKey);
        return col ? (
          <FilterPanel
            id="dt-filter-panel"
            col={col}
            rect={openPanel.rect}
            value={columnFilters[openPanel.colKey]}
            onApply={val => { setColumnFilter(openPanel.colKey, val); setOpenPanel(null); }}
            onClear={() => { clearColumnFilter(openPanel.colKey); setOpenPanel(null); }}
            onClose={() => setOpenPanel(null)}
          />
        ) : null;
      })()}
    </div>
  );
}
